import type { FaqPair } from './cityPlatformFaq';

/** FAQ locales enrichies (#799) — éduc / sitter / toiletteur × Paris, Lyon, Marseille. */
const PILOTS: Record<string, Record<string, FaqPair[]>> = {
  'educateur-canin': {
    paris: [
      {
        q: 'Comment trouver un éducateur canin à Paris sur Rintintin ?',
        a: 'Filtrez les profils autour de Paris (25 km), lisez les avis et les créneaux, puis réservez en ligne. Précisez si vous êtes en appartement ou près d’un parc (Buttes-Chaumont, Bois de Boulogne…) : le pro adapte le lieu de séance.',
      },
      {
        q: 'Puis-je réserver une première séance en ligne à Paris ?',
        a: 'Oui. Choisissez un créneau affiché sur le profil. Les conditions d’annulation sont indiquées par chaque pro avant confirmation. Voir aussi /comment-ca-marche.',
      },
      {
        q: 'Éducateur à domicile ou en terrain à Paris ?',
        a: 'Les deux existent. En centre dense, beaucoup de séances se font en rue ou petit square ; un terrain ou parc plus calme est utile pour un chien réactif. Demandez au pro ce qu’il propose dans votre arrondissement.',
      },
      {
        q: 'Que faire s’il n’y a pas de créneau proche à Paris ?',
        a: 'Élargissez le rayon, changez de jour, ou consultez d’autres profils. Vous pouvez aussi ouvrir la carte interactive depuis la page métier. Aucune garantie de dispo immédiate en zone tendue.',
      },
      {
        q: 'Quelle différence entre éducateur et comportementaliste à Paris ?',
        a: 'L’éducateur travaille plutôt obéissance et vie quotidienne ; le comportementaliste intervient surtout sur peurs, agressivité ou anxiété. Les pages métiers Rintintin décrivent chaque rôle — le choix final se fait avec le pro après échange.',
      },
      {
        q: 'Comment fonctionne Rintintin Booking ?',
        a: 'Vous comparez des profils, réservez un créneau et payez selon les modalités du pro. Guide : /comment-ca-marche.',
      },
    ],
    lyon: [
      {
        q: 'Comment trouver un éducateur canin à Lyon sur Rintintin ?',
        a: 'Parcourez les pros autour de Lyon, comparez avis et créneaux, réservez en ligne. Indiquez si vous êtes sur les pentes (Croix-Rousse, Fourvière) ou près de la Tête d’Or : le cadre change beaucoup le travail en laisse.',
      },
      {
        q: 'Puis-je réserver une première séance en ligne à Lyon ?',
        a: 'Oui, via les créneaux du profil. Lisez les conditions d’annulation avant de confirmer. Parcours détaillé : /comment-ca-marche.',
      },
      {
        q: 'Éducateur à domicile ou en parc à Lyon ?',
        a: 'Les deux. La Tête d’Or le week-end est très fréquentée : utile en socialisation avancée, souvent trop dense pour un début de réactivité. Demandez un parcours adapté à votre quartier.',
      },
      {
        q: 'Pas de créneau proche à Lyon : que faire ?',
        a: 'Élargissez le rayon (Vénissieux, Villeurbanne, Caluire… selon les profils), changez de jour, ou comparez d’autres pros sur la carte.',
      },
      {
        q: 'Éducateur ou comportementaliste à Lyon ?',
        a: 'Éducation = bases et vie quotidienne ; comportement = troubles plus marqués. Consultez les pages métiers puis validez avec le professionnel après un premier échange.',
      },
      {
        q: 'Comment fonctionne Rintintin Booking ?',
        a: 'Comparaison de profils, réservation en ligne, suivi des conditions du pro. Voir /comment-ca-marche.',
      },
    ],
    marseille: [
      {
        q: 'Comment trouver un éducateur canin à Marseille sur Rintintin ?',
        a: 'Comparez les profils autour de Marseille, avis et créneaux, puis réservez. Précisez collines, centre dense ou proximité calanques : chaleur et dénivelé changent les séances, surtout en été.',
      },
      {
        q: 'Puis-je réserver une première séance en ligne à Marseille ?',
        a: 'Oui. Sélectionnez un créneau disponible sur le profil et validez. Conditions d’annulation propres à chaque pro. Guide : /comment-ca-marche.',
      },
      {
        q: 'Éducateur à domicile ou en extérieur à Marseille ?',
        a: 'Les deux. En été, beaucoup de pros préfèrent tôt le matin pour éviter le bitume chaud. Demandez les horaires réalistes pour votre arrondissement.',
      },
      {
        q: 'Que faire sans créneau proche à Marseille ?',
        a: 'Élargissez le rayon, essayez un autre jour, ou consultez d’autres profils sur la carte. La demande estivale peut réduire les dispos immédiates.',
      },
      {
        q: 'Éducateur ou comportementaliste à Marseille ?',
        a: 'L’éducateur accompagne l’apprentissage du quotidien ; le comportementaliste traite surtout peurs et agressivité. Les fiches métiers aident à orienter, le pro confirme après contact.',
      },
      {
        q: 'Comment fonctionne Rintintin Booking ?',
        a: 'Vous réservez en ligne après avoir comparé profils et avis. Détails : /comment-ca-marche.',
      },
    ],
  },
  'pet-sitter': {
    paris: [
      {
        q: 'Comment trouver un pet-sitter à Paris sur Rintintin ?',
        a: 'Parcourez les profils autour de Paris, lisez avis et services (garde à domicile, chez le sitter, visites), puis réservez un créneau. Précisez horaires de métro / immeuble : l’accès réel compte autant que le quartier.',
      },
      {
        q: 'Puis-je réserver une première garde en ligne à Paris ?',
        a: 'Oui, si le pro affiche des créneaux. Une visite de familiarisation est souvent proposée avant un long séjour — demandez-la sur le profil ou en message.',
      },
      {
        q: 'Garde à domicile ou chez le sitter à Paris ?',
        a: 'À domicile : moins de déplacement pour le chien. Chez le sitter : souvent plus de présence humaine. Comparez selon l’appartement, les voisins et le stress du chien en transports.',
      },
      {
        q: 'Que faire s’il n’y a pas de créneau proche ?',
        a: 'Élargissez le rayon en petite couronne, changez de dates, ou consultez d’autres profils. Les week-ends et ponts se remplissent vite à Paris.',
      },
      {
        q: 'Pet-sitter et promeneur : quelle différence ?',
        a: 'Le promeneur se concentre sur les sorties ; le pet-sitter couvre plutôt garde, visites et présence. Certains profils proposent les deux — lisez les services listés.',
      },
      {
        q: 'Comment fonctionne Rintintin Booking ?',
        a: 'Comparaison, réservation en ligne, conditions propres au pro. Voir /comment-ca-marche.',
      },
    ],
    lyon: [
      {
        q: 'Comment trouver un pet-sitter à Lyon sur Rintintin ?',
        a: 'Comparez les profils autour de Lyon, services et avis, puis réservez. Indiquez pentes, centre ou périphérie : les temps de trajet changent la faisabilité des visites midi.',
      },
      {
        q: 'Puis-je réserver une première garde en ligne à Lyon ?',
        a: 'Oui via les créneaux du profil. Une rencontre préalable est souvent utile avant plusieurs jours d’absence.',
      },
      {
        q: 'Garde à domicile ou chez le sitter à Lyon ?',
        a: 'À domicile limite le stress du trajet ; chez le sitter peut offrir plus de compagnie. Choisissez selon votre chien et votre quartier (centre vs périphérie).',
      },
      {
        q: 'Pas de créneau proche à Lyon : que faire ?',
        a: 'Élargissez le rayon métropolitain, décalez les dates, ou ouvrez la carte pour d’autres profils.',
      },
      {
        q: 'Pet-sitter ou promeneur à Lyon ?',
        a: 'Promeneur = sorties ; pet-sitter = garde / présence. Vérifiez les prestations affichées sur chaque fiche.',
      },
      {
        q: 'Comment fonctionne Rintintin Booking ?',
        a: 'Réservation en ligne après comparaison des profils. Guide : /comment-ca-marche.',
      },
    ],
    marseille: [
      {
        q: 'Comment trouver un pet-sitter à Marseille sur Rintintin ?',
        a: 'Parcourez les profils autour de Marseille, comparez avis et services, réservez en ligne. En été, demandez des sorties tôt le matin : chaleur et bitume comptent.',
      },
      {
        q: 'Puis-je réserver une première garde en ligne à Marseille ?',
        a: 'Oui si des créneaux sont ouverts. Une visite de familiarisation reste recommandée avant un long séjour ou des absences répétées.',
      },
      {
        q: 'Garde à domicile ou chez le sitter à Marseille ?',
        a: 'À domicile évite les trajets en colline ou embouteillages ; chez le sitter peut offrir plus de présence. Adaptez au stress du chien et à votre arrondissement.',
      },
      {
        q: 'Que faire sans créneau proche à Marseille ?',
        a: 'Élargissez le rayon, changez de dates (été = forte demande), ou comparez d’autres profils sur la carte.',
      },
      {
        q: 'Pet-sitter ou promeneur à Marseille ?',
        a: 'Le promeneur gère surtout les sorties ; le pet-sitter la garde et les visites. Certains cumulent — lisez la fiche.',
      },
      {
        q: 'Comment fonctionne Rintintin Booking ?',
        a: 'Vous réservez après avoir comparé profils et avis. Voir /comment-ca-marche.',
      },
    ],
  },
  'toiletteur-canin': {
    paris: [
      {
        q: 'Comment trouver un toiletteur canin à Paris sur Rintintin ?',
        a: 'Comparez les profils autour de Paris, prestations (bain, coupe, hygienique) et avis, puis réservez un créneau. Précisez race et état du pelage : les dispos salon vs mobile diffèrent.',
      },
      {
        q: 'Puis-je réserver un premier toilettage en ligne à Paris ?',
        a: 'Oui, si le pro ouvre des créneaux. Pour un chien anxieux, mentionnez-le : certains préfèrent des séances plus courtes au début.',
      },
      {
        q: 'Toiletteur en salon ou mobile à Paris ?',
        a: 'Salon = poste fixe ; mobile = intervention à domicile (accès immeuble, eau, stationnement). Vérifiez ce que chaque profil propose dans votre arrondissement.',
      },
      {
        q: 'Pas de créneau proche à Paris ?',
        a: 'Élargissez le rayon, essayez un autre jour, ou consultez d’autres toiletteurs sur la carte. Les week-ends se remplissent vite.',
      },
      {
        q: 'À quelle fréquence toilettage à Paris ?',
        a: 'Ça dépend race, pelage et saison — demandez au pro après la première visite. Les guides blog toiletteur sur Rintintin aident à préparer la séance, sans remplacer son avis.',
      },
      {
        q: 'Comment fonctionne Rintintin Booking ?',
        a: 'Réservation en ligne après comparaison. Parcours : /comment-ca-marche. Guides : cluster blog toiletteur.',
      },
    ],
    lyon: [
      {
        q: 'Comment trouver un toiletteur canin à Lyon sur Rintintin ?',
        a: 'Parcourez les profils autour de Lyon, comparez prestations et avis, réservez en ligne. Indiquez si vous préférez salon ou déplacement à domicile selon votre quartier.',
      },
      {
        q: 'Puis-je réserver un premier toilettage en ligne à Lyon ?',
        a: 'Oui via les créneaux affichés. Signalez un chien sensible au bruit ou à la manipulation pour adapter la première séance.',
      },
      {
        q: 'Salon ou mobile à Lyon ?',
        a: 'Les deux coexistent. En pente ou sans parking, le mobile peut simplifier ; le salon offre un poste équipé. Lisez la fiche du pro.',
      },
      {
        q: 'Que faire sans créneau proche à Lyon ?',
        a: 'Élargissez le rayon métropolitain, changez de jour, ou comparez d’autres profils.',
      },
      {
        q: 'Fréquence de toilettage à Lyon ?',
        a: 'Variable selon pelage et saison. Le pro conseille après avoir vu le chien ; les articles blog toiletteur aident à préparer la visite.',
      },
      {
        q: 'Comment fonctionne Rintintin Booking ?',
        a: 'Comparaison de profils puis réservation en ligne. Voir /comment-ca-marche.',
      },
    ],
    marseille: [
      {
        q: 'Comment trouver un toiletteur canin à Marseille sur Rintintin ?',
        a: 'Comparez les profils autour de Marseille, prestations et avis, puis réservez. En été, le pelage et la chaleur influencent le confort : mentionnez-le au pro.',
      },
      {
        q: 'Puis-je réserver un premier toilettage en ligne à Marseille ?',
        a: 'Oui si des créneaux sont ouverts. Pour un premier contact, une séance plus courte peut être proposée selon le pro.',
      },
      {
        q: 'Salon ou mobile à Marseille ?',
        a: 'Salon fixe ou passage à domicile. Accès, eau et stationnement varient selon les collines et arrondissements — vérifiez la fiche.',
      },
      {
        q: 'Pas de créneau proche à Marseille ?',
        a: 'Élargissez le rayon, décalez la date, ou consultez d’autres toiletteurs sur la carte.',
      },
      {
        q: 'Fréquence de toilettage à Marseille ?',
        a: 'Selon race, pelage et saison (chaleur, baignades). Demandez conseil au pro ; les guides blog toiletteur préparent la séance sans remplacer son avis.',
      },
      {
        q: 'Comment fonctionne Rintintin Booking ?',
        a: 'Vous réservez en ligne après comparaison. Guide : /comment-ca-marche.',
      },
    ],
  },
};

export const CITY_LANDING_FAQ_PILOT_METIERS = [
  'educateur-canin',
  'pet-sitter',
  'toiletteur-canin',
] as const;

export const CITY_LANDING_FAQ_PILOT_CITIES = ['paris', 'lyon', 'marseille'] as const;

export function getCityMetierFaqPilot(metierSlug: string, citySlug: string): FaqPair[] | undefined {
  return PILOTS[metierSlug]?.[citySlug];
}
